/**
 * Простой человека, а не простой сессии.
 *
 * Бэк закрывает окно активности по тишине в запросах, но панель шумит сама:
 * карточки сделок и транзакций опрашивают API раз в 15 секунд (голыми
 * setInterval, которые браузер в скрытой вкладке лишь притормаживает до одного
 * раза в минуту), рядом живут refetchInterval у react-query. Оставленная
 * открытой панель продлевала окно бесконечно, и кода не спрашивали никогда —
 * ровно это и выглядело как «таймер не работает».
 *
 * Кто здесь человек, а кто таймер, знает только фронт. Этот модуль считает
 * активностью ввод, клики, прокрутку и движение мыши — и ничего больше.
 *
 * Счётчик общий на все вкладки через localStorage: иначе вкладка, в которой
 * человек не работал последние 15 минут, заперла бы сессию, пока он работает
 * в соседней. Хранилище может быть недоступно (приватный режим, запрет на
 * сайт) — тогда каждая вкладка живёт своим счётчиком в памяти, что не хуже
 * прежнего поведения.
 */

/** Сколько человек может ничего не делать, прежде чем панель запрётся. */
export const IDLE_LIMIT_MS = 15 * 60 * 1000;

/**
 * Окно, внутри которого заявка вкладки на замок считается свежей.
 *
 * Запирает сессию одна вкладка из всех: у каждой свой таймер, и все они
 * замолчали одновременно. Без заявки в Telegram уедет столько кодов, сколько
 * вкладок открыто, а рабочим будет последний.
 */
const CLAIM_TTL_MS = 60 * 1000;

const ACTIVITY_KEY = "garant-admin:last-activity";
const CLAIM_KEY = "garant-admin:idle-lock-claim";
const VERIFIED_KEY = "garant-admin:reverified-at";

/** Чаще этого в хранилище не пишем: событий активности сотни в минуту. */
const WRITE_THROTTLE_MS = 5000;

/**
 * События, которые считаем признаком живого человека.
 *
 * Намеренно без `visibilitychange` и `focus`: вернуться к вкладке после часа
 * отсутствия — это не работа в панели, а как раз момент, когда код и нужно
 * спросить. Переключение вкладки только запускает проверку, но отсчёт не
 * сбрасывает.
 */
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "wheel",
  "keydown",
  "touchstart",
  "scroll",
] as const;

let memoryActivityAt = 0;
let lastWriteAt = 0;

function readStorage(key: string): number {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return 0;
    const value = Number(raw);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function writeStorage(key: string, value: number): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* хранилище недоступно — остаёмся на счётчике в памяти */
  }
}

/** Человек что-то сделал: клик, ввод, прокрутка, удачный вход, ввод кода. */
export function markActivity(now: number = Date.now()): void {
  memoryActivityAt = now;
  if (now - lastWriteAt < WRITE_THROTTLE_MS) return;
  lastWriteAt = now;
  writeStorage(ACTIVITY_KEY, now);
}

/** Момент последней активности в любой из вкладок; 0 — если её не было. */
export function lastActivityAt(): number {
  return Math.max(memoryActivityAt, readStorage(ACTIVITY_KEY));
}

/** Молчит ли человек дольше `IDLE_LIMIT_MS`. */
export function isIdle(now: number = Date.now()): boolean {
  const last = lastActivityAt();
  if (!last) return false;
  return now - last >= IDLE_LIMIT_MS;
}

/**
 * Заявить, что замок ставит эта вкладка. `false` — уже взялась соседняя.
 *
 * Гонка двух вкладок теоретически возможна — атомарного CAS в localStorage
 * нет, — но стоит она одного лишнего кода в Telegram, а не сломанного входа:
 * сама ручка замка идемпотентна.
 */
export function claimIdleLock(now: number = Date.now()): boolean {
  const claimedAt = readStorage(CLAIM_KEY);
  if (claimedAt && now - claimedAt < CLAIM_TTL_MS) return false;
  writeStorage(CLAIM_KEY, now);
  return true;
}

/** Продлить заявку: вкладка всё ещё держит окно с кодом. */
export function refreshIdleClaim(now: number = Date.now()): void {
  writeStorage(CLAIM_KEY, now);
}

/** Отдать заявку: запереть не вышло, пусть попробует кто угодно. */
export function releaseIdleLock(): void {
  try {
    window.localStorage.removeItem(CLAIM_KEY);
  } catch {
    /* см. writeStorage */
  }
}

/** Сообщить соседним вкладкам, что код принят и сессия снова открыта. */
export function announceVerified(now: number = Date.now()): void {
  writeStorage(VERIFIED_KEY, now);
}

/** Подписка на подтверждение из соседней вкладки. Возвращает отписку. */
export function subscribeVerified(handler: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === VERIFIED_KEY && event.newValue) handler();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

/** Подписка на действия человека. Возвращает отписку. */
export function subscribeActivity(handler: () => void): () => void {
  const options: AddEventListenerOptions = { passive: true, capture: true };
  ACTIVITY_EVENTS.forEach((event) => {
    window.addEventListener(event, handler, options);
  });
  return () => {
    ACTIVITY_EVENTS.forEach((event) => {
      window.removeEventListener(event, handler, options);
    });
  };
}

/** Только для тестов: сбросить счётчики в памяти. */
export function resetIdleStateForTests(): void {
  memoryActivityAt = 0;
  lastWriteAt = 0;
}
